"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { SaveButton } from "@/components/places/SaveButton";

type Rec = { slug: string; name: string; region: string; image: string; blurb: string };
type NearRec = Rec & { distanceKm: number };
type Festival = { slug: string; name: string; season: string; region: string };
type Payload = {
  personalized: boolean;
  season: string;
  note: string;
  items: Rec[];
  nearby?: NearRec[];
  nearLabel?: string;
  upcoming?: Festival[];
  alsoViewed?: Rec[];
  alsoAnchor?: string;
  savedSlugs?: string[];
};

/**
 * Personalized strip. Renders client-side from the visitor's cookie profile so
 * the homepage itself stays cacheable; only this strip is per-visitor.
 */
export function ForYouStrip() {
  const [data, setData] = useState<Payload | null>(null);

  useEffect(() => {
    let alive = true;
    fetch("/api/recommendations")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive) setData(d);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!data || data.items.length === 0) return null;

  // Heading stays "Recommended for You" either way (release spec Part 11) -
  // the eyebrow line right above it is what honestly discloses whether this
  // is actually personalized or a generic seasonal fallback, so the pair
  // together never overstates what the site knows about this visitor.
  const heading = "Recommended for You";
  const eyebrow = data.personalized
    ? "Based on what you've been exploring"
    : "Seasonal picks across Pakistan";

  // Single grid for all items - splitting into two 3-item groups left an
  // orphaned empty cell at 2-column widths (3 doesn't divide evenly by 2).
  const items = data.items.slice(0, 6);

  return (
    <section className="pbd-section pbd-container">
      <div className="mb-7 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="pbd-eyebrow">{eyebrow}</p>
          <h2 className="pbd-h2 mt-1">{heading}</h2>
          <p className="mt-2 mb-0 max-w-[620px] text-[0.98rem] leading-relaxed text-[var(--muted)]">
            {data.note}
          </p>
        </div>
        <Link href="/places" className="pbd-link-muted hidden sm:inline">
          All field guides →
        </Link>
      </div>

      {data.upcoming && data.upcoming.length > 0 ? (
        <div className="pbd-no-scrollbar mb-6 flex max-w-full items-center gap-2.5 overflow-x-auto pb-1">
          <span className="shrink-0 text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--muted)]">
            Coming up
          </span>
          {data.upcoming.map((f) => (
            <Link
              key={f.slug}
              href={`/events/${f.slug}`}
              className="shrink-0 rounded-full border border-[color-mix(in_srgb,var(--accent)_35%,transparent)] bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] px-3 py-1.5 text-[0.78rem] font-semibold text-[var(--accent)] no-underline transition-colors hover:bg-[color-mix(in_srgb,var(--accent)_20%,transparent)]"
            >
              {f.name} · {f.season}
            </Link>
          ))}
        </div>
      ) : null}

      <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((r) => (
          <ForYouTile
            key={r.slug}
            rec={r}
            aspect="aspect-[4/5]"
            saved={data.savedSlugs?.includes(r.slug) ?? false}
          />
        ))}
      </div>

      {data.alsoViewed && data.alsoViewed.length > 0 ? (
        <div className="mt-8">
          <p className="m-0 mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--muted)]">
            More like this
          </p>
          <div className="flex flex-wrap gap-2.5">
            {data.alsoViewed.map((n) => (
              <Link
                key={n.slug}
                href={`/places/${n.slug}`}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-soft)] bg-[var(--surface)] px-3 py-1.5 text-[0.82rem] no-underline text-[var(--foreground)] transition-colors hover:border-[var(--accent)]"
              >
                <span className="font-semibold">{n.name}</span>
                <span className="text-[var(--muted)]">· {n.region}</span>
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      {data.nearby && data.nearby.length > 0 ? (
        <div className="mt-8 rounded-lg border border-[var(--border-subtle)] bg-[color-mix(in_srgb,var(--surface)_45%,transparent)] p-5">
          <p className="m-0 mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--accent)]">
            {data.nearLabel || "Closest to you"}
          </p>
          <div className="flex flex-wrap gap-2.5">
            {data.nearby.map((n) => (
              <Link
                key={n.slug}
                href={`/places/${n.slug}`}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-soft)] bg-[var(--surface)] px-3 py-1.5 text-[0.82rem] no-underline text-[var(--foreground)] transition-colors hover:border-[var(--accent)]"
              >
                <span className="font-semibold">{n.name}</span>
                <span className="text-[var(--muted)]">· {n.distanceKm} km</span>
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ForYouTile({ rec, aspect, saved }: { rec: Rec; aspect: string; saved: boolean }) {
  return (
    <div className="group relative">
      <Link href={`/places/${rec.slug}`} className="block no-underline">
        <div className={`relative ${aspect} overflow-hidden rounded-lg border border-[var(--border-subtle)]`}>
          <Image
            src={rec.image || "/images/hunza-valley-rakaposhi.webp"}
            alt={rec.name}
            fill
            className="object-cover transition-transform duration-700 group-hover:scale-105"
            sizes="(max-width:980px) 100vw, 33vw"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
        <div className="pt-3.5">
          <p className="m-0 text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--accent)]">{rec.region}</p>
          <h3 className="mt-1.5 mb-0 font-[family-name:var(--font-display)] text-[1.2rem] font-medium text-[var(--foreground)] group-hover:text-[var(--accent)]">
            {rec.name}
          </h3>
          <p className="mt-1.5 mb-0 text-[13px] leading-snug text-[var(--muted)]">{rec.blurb}</p>
        </div>
      </Link>
      <SaveButton slug={rec.slug} initialSaved={saved} variant="icon" className="absolute right-3 top-3" />
    </div>
  );
}
